import { useContext } from 'react'

import { Box, Grid, Typography } from '@material-ui/core'
import { Info } from '@scoir/tooltips'
import { useSelector } from 'react-redux'

import { isDisplayParentYearMetricsEnabled } from 'store/feature-toggle'

import PremiumInsightsContext from '../PremiumInsightsProvider/Context'
import { METRIC_TITLES, METRIC_TOOLTIPS, PREVIOUS_DATE_RANGE_METRIC_DESCRIPTORS } from '../constants'
import InsightsMetric, {
  NullDelta,
  PrimaryCountDelta,
  SecondaryDeltaPercentage,
  SecondaryValue,
  formatPercentage,
  formatTotal,
} from './InsightsMetric'

export default function InsightsReach() {
  const { getCollegePremiumInsightsRequest, selectedFilters } = useContext(PremiumInsightsContext)
  const gridItemWidth = selectedFilters.audience === 'student' ? 4 : 6

  const isParentAudience = selectedFilters.audience === 'parent' || selectedFilters.audience === 'all'
  const isAcademicYearFilter = selectedFilters.bucket === '1y'
  const displayParentYearMetrics = useSelector((state) => isDisplayParentYearMetricsEnabled(state))

  const hideParentDelta = !displayParentYearMetrics && isParentAudience && isAcademicYearFilter
  const tooltipText =
    "We're still gathering year-over-year data for Parents. Comparable insights will be available soon."

  return (
    <Box>
      <Box
        mb={2}
        display="flex"
        justifyContent="space-between"
      >
        <Typography
          variant="subtitle2"
          component="h3"
        >
          Reach
        </Typography>
        {hideParentDelta && (
          <Box
            display="flex"
            alignItems="center"
            gridGap={8}
          >
            <Info
              tooltipProps={{ placement: 'bottom' }}
              aria-label={tooltipText}
            >
              {tooltipText}
            </Info>
            <Typography variant="body2">Coming Soon: More Parent Data</Typography>
          </Box>
        )}
      </Box>
      <Grid
        container
        spacing={2}
      >
        {getCollegePremiumInsightsRequest.status === 'success' && (
          <>
            <Grid
              item
              md={gridItemWidth}
              xs={12}
            >
              <InsightsMetric
                title={METRIC_TITLES.IMPRESSIONS}
                tooltipText={METRIC_TOOLTIPS.IMPRESSIONS}
                primaryValue={
                  <Typography
                    variant="h5"
                    component="div"
                  >
                    {formatTotal(getCollegePremiumInsightsRequest.data.countMetrics.impressions.count)}
                  </Typography>
                }
                secondaryValue={
                  hideParentDelta ? (
                    <NullDelta
                      label={PREVIOUS_DATE_RANGE_METRIC_DESCRIPTORS[selectedFilters.bucket]}
                      isPercentage
                    />
                  ) : (
                    <SecondaryDeltaPercentage
                      percentage={getCollegePremiumInsightsRequest.data.countMetrics.impressions.countPercentChange}
                      label={PREVIOUS_DATE_RANGE_METRIC_DESCRIPTORS[selectedFilters.bucket]}
                    />
                  )
                }
              />
            </Grid>
            <Grid
              item
              md={gridItemWidth}
              xs={12}
            >
              <InsightsMetric
                title={METRIC_TITLES.USERS_REACHED}
                tooltipText={METRIC_TOOLTIPS.USERS_REACHED}
                primaryValue={
                  <Typography
                    variant="h5"
                    component="div"
                  >
                    {formatTotal(getCollegePremiumInsightsRequest.data.countMetrics.usersReached.count)}
                  </Typography>
                }
                secondaryValue={
                  hideParentDelta ? (
                    <NullDelta
                      label={PREVIOUS_DATE_RANGE_METRIC_DESCRIPTORS[selectedFilters.bucket]}
                      isPercentage
                    />
                  ) : (
                    <SecondaryDeltaPercentage
                      percentage={getCollegePremiumInsightsRequest.data.countMetrics.usersReached.countPercentChange}
                      label={PREVIOUS_DATE_RANGE_METRIC_DESCRIPTORS[selectedFilters.bucket]}
                    />
                  )
                }
              />
            </Grid>
            {selectedFilters.audience === 'student' && (
              <Grid
                item
                md={gridItemWidth}
                xs={12}
              >
                <InsightsMetric
                  title={METRIC_TITLES.FOLLOW_COUNT}
                  tooltipText={METRIC_TOOLTIPS.FOLLOW_COUNT}
                  primaryValue={
                    <PrimaryCountDelta value={getCollegePremiumInsightsRequest.data.countMetrics.follows.count} />
                  }
                  secondaryValue={
                    <Box
                      display="flex"
                      alignItems="center"
                    >
                      <SecondaryValue
                        value={formatPercentage(getCollegePremiumInsightsRequest.data.rateMetrics.follows.rate)}
                        label={METRIC_TITLES.FOLLOW_RATE}
                      />
                      <Box mx={1}>
                        <Info
                          outlinedIcon
                          inheritColor
                          tooltipProps={{ placement: 'top' }}
                          aria-label={METRIC_TOOLTIPS.FOLLOW_RATE}
                        >
                          {METRIC_TOOLTIPS.FOLLOW_RATE}
                        </Info>
                      </Box>
                    </Box>
                  }
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Box>
  )
}
